import {ActionIcon, Button, Table} from '@mantine/core';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import HelpButton from 'help/HelpButton';
import notifications from 'notifications';
import {APIKey, createAPIKey, revokeAPIKey} from 'profile/APIKeysDAO';
import RegenerateAPIKeyConfirmModal from 'profile/RegenerateAPIKeyConfirmModal';
import SuccessfulAPIKeyCreationModal from 'profile/SuccessfulAPIKeyCreationModal';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import PreferenceCheckbox from 'ui-library/inputs/PreferenceCheckbox';

import APIBearerTokenSection from './APIBearerTokenSection';
import V3ExplorerBearerTokenSection from './V3ExplorerBearerTokenSection';

interface APIKeyRowProps {
  apiKey: APIKey;
  onRevoke: (apiKey: APIKey) => void;
}

function APIKeyRow({apiKey, onRevoke}: APIKeyRowProps): JSX.Element {
  const handleRevoke = (): void => {
    onRevoke(apiKey);
  };

  return (
    <tr>
      <td>{`• • • • • • • • ${apiKey.key_hint}`}</td>
      <td>{apiKey.created_at}</td>
      <td>{apiKey.expires_at ?? 'Never'}</td>
      <td>
        <ActionIcon aria-label="Revoke API key" color="red" variant="subtle" onClick={handleRevoke}>
          <BenchlingIcon icon="delete" />
        </ActionIcon>
      </td>
    </tr>
  );
}

function APIKeysSection(): JSX.Element {
  const [apiKeys, setApiKeys] = useState<Array<APIKey>>(() => window.bootstrap?.api_keys ?? []);
  // The plaintext secret of the just-created key (plus its expiration), held only to populate the one-time
  // creation modal (the secret cannot be retrieved later). Cleared when the modal is dismissed so the secret
  // does not linger in state.
  const [createdKey, setCreatedKey] = useState<{secret: string; expiresAt: string | null} | null>(null);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);

  const generateKey = async (): Promise<void> => {
    const newKey = await createAPIKey();
    // Strip the plaintext secret before storing the key in the list that backs the table; only the
    // one-time modal needs it. The table renders the masked key_hint, not the secret.
    setApiKeys([{...newKey, key: ''}]);
    setCreatedKey({secret: newKey.key, expiresAt: newKey.expires_at});
  };

  const generate = (): void => {
    generateKey().catch(notifications.notifyError);
  };

  const handleGenerate = (): void => {
    if (apiKeys.length > 0) {
      setConfirmingRegenerate(true);
    } else {
      generate();
    }
  };

  const revokeKey = async (apiKey: APIKey): Promise<void> => {
    await revokeAPIKey(apiKey.id);
    setApiKeys((keys) => keys.filter((key) => key.id !== apiKey.id));
    notifications.success('API Key revoked successfully.');
  };

  const handleRevoke = (apiKey: APIKey): void => {
    revokeKey(apiKey).catch(notifications.notifyError);
  };

  const hasKeys = apiKeys.length > 0;

  return (
    <>
      <h3>Keys</h3>
      {hasKeys && (
        <Table>
          <thead>
            <tr>
              <th>Key</th>
              <th>Created At</th>
              <th>Expires At</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {apiKeys.map((apiKey) => (
              <APIKeyRow key={apiKey.id} apiKey={apiKey} onRevoke={handleRevoke} />
            ))}
          </tbody>
        </Table>
      )}
      <Button variant="tertiary" onClick={handleGenerate}>
        {hasKeys ? 'Regenerate API Key' : 'Generate New API Key'}
      </Button>
      {confirmingRegenerate && (
        <RegenerateAPIKeyConfirmModal
          onConfirm={() => {
            setConfirmingRegenerate(false);
            generate();
          }}
          onRequestClose={() => {
            setConfirmingRegenerate(false);
          }}
        />
      )}
      {createdKey != null && (
        <SuccessfulAPIKeyCreationModal
          apiKey={createdKey.secret}
          expiresAt={createdKey.expiresAt}
          onRequestClose={() => {
            setCreatedKey(null);
          }}
        />
      )}
    </>
  );
}

function APICredentialsSection(): JSX.Element {
  return <>{window.bootstrap?.useBearerTokenAuthAPI ? <APIBearerTokenSection /> : <APIKeysSection />}</>;
}

function ProfileAPISection(): JSX.Element {
  return (
    <>
      <h2>API</h2>
      <APICredentialsSection />
      {BenchlingConfig.CAPILLARY_ENABLE_V3_UNIFIED_API && <V3ExplorerBearerTokenSection />}
      <h3>Documentation</h3>
      <div>
        Get started with the API at{' '}
        <a
          title="docs.benchling.com"
          href="https://docs.benchling.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          docs.benchling.com
        </a>
        .
      </div>
      <div>
        Access the API reference with testing abilities at{' '}
        <a
          title="docs.benchling.com/docs/api"
          href="https://docs.benchling.com/docs/api"
          target="_blank"
          rel="noopener noreferrer"
        >
          docs.benchling.com/docs/api
        </a>
        .
      </div>

      <h3>View IDs</h3>
      <div className="form-group">
        <div className="u-flex-display">
          <PreferenceCheckbox
            id="copyAPIIDs"
            label="Enable “Copy API ID” button"
            preferenceKey="ENABLE_COPY_API_ID"
          />
          <HelpButton
            renderContent={() => (
              <a
                title="Guide for using this feature"
                href="https://docs.benchling.com/docs/getting-started-1#copy-api-ids"
                target="_blank"
                rel="noopener noreferrer"
              >
                Guide for using this feature
              </a>
            )}
            iconClassName="u-tiny-spacing-left"
            placement={FloatingPlacement.TOP}
          />
        </div>
      </div>
    </>
  );
}

export default ProfileAPISection;
